import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type PropsWithChildren } from "react";

import { fetchCurrentUser, type CurrentUser } from "../../api/auth";
import { ApiError } from "../../api/client";

interface AuthState {
  user: CurrentUser | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
  clear: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["current-user"],
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 401) && failureCount < 1,
    staleTime: 60_000,
  });

  return (
    <AuthContext.Provider
      value={{
        user: query.data ?? null,
        isLoading: query.isPending,
        refresh: async () => {
          await queryClient.invalidateQueries({ queryKey: ["current-user"] });
        },
        clear: () => queryClient.setQueryData(["current-user"], null),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook lives beside its provider so their contract cannot drift.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return value;
}
